import React, { useState } from 'react';
import {
  X,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Lock,
  Trash2,
  Building,
  Calendar,
  Layers,
  IndianRupee,
  CheckSquare,
  Square
} from 'lucide-react';
import { ProjectTask, formatToDDMMYY } from './types.ts';

interface DeleteMultipleJobsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProjects: ProjectTask[];
  isAdmin: boolean;
  onConfirmDeleteMultiple: (projectIds: string[]) => void;
  onAdminUnlockSuccess?: () => void;
  onDeselectJob?: (projectId: string) => void;
}

export const DeleteMultipleJobsModal: React.FC<DeleteMultipleJobsModalProps> = ({
  isOpen,
  onClose,
  selectedProjects,
  isAdmin,
  onConfirmDeleteMultiple,
  onAdminUnlockSuccess,
  onDeselectJob
}) => {
  const [pin, setPin] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || selectedProjects.length === 0) return null;

  const validPins = ['1234', 'admin123', 'dvepl', 'dinesh'];

  const totalValue = selectedProjects.reduce((sum, p) => sum + (p.totalAmount || p.amount || 0), 0);

  const handleDelete = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // If not admin, require admin PIN
    if (!isAdmin) {
      if (!validPins.includes(pin.trim().toLowerCase())) {
        setError('Invalid Admin PIN. Only Dinesh Sir / Super Admin can authorize bulk job deletion.');
        return;
      }
      if (onAdminUnlockSuccess) {
        onAdminUnlockSuccess();
      }
    } else {
      // If admin, require confirmation keyword "DELETE"
      if (confirmText.trim().toUpperCase() !== 'DELETE') {
        setError('Please type DELETE in the box below to confirm permanent bulk deletion.');
        return;
      }
    }

    setIsDeleting(true);
    setTimeout(() => {
      onConfirmDeleteMultiple(selectedProjects.map(p => p.id));
      setIsDeleting(false);
      setPin('');
      setConfirmText('');
      onClose();
    }, 200);
  };

  const handleClose = () => {
    setPin('');
    setConfirmText('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/65 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-2xl border border-red-200 max-w-xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Danger Header */}
        <div className="bg-gradient-to-r from-red-700 via-rose-700 to-red-800 p-5 text-white flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-red-100 border border-white/20 shadow-xs shrink-0">
              <ShieldAlert className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-bold text-base text-white">
                  Delete Multiple Selected Jobs
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-black bg-red-900/80 text-red-100 border border-red-400/40 uppercase tracking-wider">
                  Admin Restricted
                </span>
              </div>
              <p className="text-xs text-red-100">
                You are about to permanently remove <span className="font-bold underline">{selectedProjects.length} selected orders</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4 max-h-[82vh] overflow-y-auto">
          {/* Quick summary stats */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-red-50/70 border border-red-200 rounded-xl text-xs">
            <div>
              <span className="text-slate-500 font-semibold block text-[11px]">Selected Jobs:</span>
              <span className="text-sm font-black text-red-700 font-mono">
                {selectedProjects.length} {selectedProjects.length === 1 ? 'Job' : 'Jobs'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[11px]">Combined Order Value:</span>
              <span className="text-sm font-black text-slate-800 font-mono">
                ₹{totalValue.toLocaleString('en-IN')}
              </span>
            </div>
          </div>

          {/* List of jobs to be deleted */}
          <div>
            <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-2">
              <span>Jobs queued for deletion:</span>
              <span className="text-[11px] font-medium text-slate-500">Uncheck to preserve</span>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-slate-200 border border-slate-200 rounded-xl bg-slate-50/50">
              {selectedProjects.map((p) => (
                <div key={p.id} className="p-2.5 flex items-center justify-between hover:bg-white transition-colors text-xs">
                  <div className="flex items-center space-x-2.5 min-w-0">
                    {onDeselectJob && (
                      <button
                        type="button"
                        onClick={() => onDeselectJob(p.id)}
                        className="text-red-500 hover:text-slate-400 p-0.5 cursor-pointer"
                        title="Remove from deletion selection"
                      >
                        <CheckSquare className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                    <span className="px-2 py-0.5 rounded text-[11px] font-mono font-bold bg-slate-200 text-slate-800 shrink-0">
                      DVEPL-{p.dveplCode || p.sNo}
                    </span>
                    <div className="truncate min-w-0">
                      <span className="font-bold text-slate-900 block truncate">{p.partyName}</span>
                      <span className="text-[11px] text-slate-500 block truncate">{p.itemDescription}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="font-mono text-[11px] font-bold text-slate-700 block">
                      ₹{(p.totalAmount || p.amount || 0).toLocaleString('en-IN')}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                      {p.stage}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Warning Message Box */}
          <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-xs text-red-950 space-y-1.5">
            <div className="font-bold flex items-center space-x-1.5 text-red-800">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
              <span>Bulk Deletion Notice: This action cannot be undone!</span>
            </div>
            <p className="text-red-900 leading-relaxed text-[11px]">
              Deleting these {selectedProjects.length} orders will permanently remove all associated drawings, stage milestones, vendor POs, BOM items, and time tracking entries.
            </p>
          </div>

          {/* Form with Permission Checks */}
          <form onSubmit={handleDelete} className="space-y-4">
            {!isAdmin ? (
              /* Non-admin user: Must enter Admin PIN */
              <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 space-y-3">
                <div className="flex items-start space-x-2.5">
                  <Lock className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900">
                    <span className="font-bold block text-amber-950 mb-0.5">
                      Admin Permission Verification Required
                    </span>
                    Only Dinesh Sir or an authorized Administrator has permission to delete multiple jobs simultaneously. Enter your Admin PIN to proceed:
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-800 mb-1">
                    Enter Admin Security PIN / Password:
                  </label>
                  <input
                    type="password"
                    autoFocus
                    value={pin}
                    onChange={(e) => {
                      setPin(e.target.value);
                      setError(null);
                    }}
                    placeholder="Enter Admin PIN (e.g. 1234 or admin123)"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:border-red-500 shadow-2xs"
                  />
                </div>
              </div>
            ) : (
              /* Admin user: Already unlocked, requires explicit safety confirmation */
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center space-x-2 text-xs font-bold text-emerald-800">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Admin Mode Active (Dinesh Sir / Super Admin)</span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Type <span className="text-red-600 font-mono font-black">DELETE</span> to confirm bulk removal of {selectedProjects.length} jobs:
                  </label>
                  <input
                    type="text"
                    autoFocus
                    value={confirmText}
                    onChange={(e) => {
                      setConfirmText(e.target.value);
                      setError(null);
                    }}
                    placeholder="Type DELETE"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold tracking-wider uppercase focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:border-red-500 shadow-2xs"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-100/80 border border-red-300 rounded-lg text-xs font-semibold text-red-900 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-2.5 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isDeleting}
                className="px-4 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isDeleting || (isAdmin && confirmText.trim().toUpperCase() !== 'DELETE')}
                className={`inline-flex items-center px-4 py-2 text-xs font-bold text-white rounded-lg shadow-md transition-all cursor-pointer ${
                  isAdmin && confirmText.trim().toUpperCase() !== 'DELETE'
                    ? 'bg-red-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 active:scale-98'
                }`}
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                <span>
                  {isDeleting
                    ? `Deleting ${selectedProjects.length} Jobs...`
                    : `Permanently Delete ${selectedProjects.length} Selected Jobs`}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
